import { HttpApi } from '@/common/http';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  IActionInspectData,
  IContourAction,
} from '@/features/stewardship/models/IStewardship';
import {
  cn,
  getInspectionRateStatus,
  getRegionActionStatus,
} from '@/lib/utils';
import { AxiosResponse } from 'axios';
import { format } from 'date-fns';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePreview } from './ImagePreview';

const api = new HttpApi();

const fetchFilesAsBase64 = async (fileIds: string[]) => {
  const results = await Promise.all(
    fileIds.map(async (fileId) => {
      try {
        const response = (await api.get(
          `/api/files/download_as_buffer/${fileId}`,
          {},
        )) as AxiosResponse;
        const contentType = response.headers?.['content-type'] || 'image/jpeg';
        const dataUrl = `data:${contentType};base64,${response?.data}`;
        return {
          fileId,
          base64: dataUrl,
          contentType,
        };
      } catch (error) {
        console.error(`Xato! ${fileId} faylini base64 ga o'tkazishda:`, error);
        return { fileId, base64: null, contentType: null };
      }
    }),
  );
  return results.filter((file) => file.base64);
};

interface ActionInspectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedAction: IContourAction | null;
  actionInspectData: IActionInspectData | null;
  actionInspectLoading: boolean;
  onClear?: () => void;
}

export const ActionInspectDialog: FC<ActionInspectDialogProps> = ({
  open,
  onOpenChange,
  selectedAction,
  actionInspectData,
  actionInspectLoading,
  onClear,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<
    { fileId: string; base64: string; contentType: string }[]
  >([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null,
  );
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClear?.();
      setFiles([]);
      setSelectedImageIndex(null);
      setIsImageDialogOpen(false);
    }
    onOpenChange(isOpen);
  };

  useEffect(() => {
    const loadFiles = async () => {
      const allFiles =
        actionInspectData?.reviews?.flatMap(
          (review) => review.file_names || [],
        ) || [];

      if (allFiles.length === 0) {
        setFiles([]);
        return;
      }

      setIsLoadingFiles(true);
      const fileIds = allFiles.map((file) => file.file_id);
      const result = await fetchFilesAsBase64(fileIds);
      const filtered = result?.filter(
        (
          file,
        ): file is { fileId: string; base64: string; contentType: string } =>
          file.base64 !== null && file.contentType !== null,
      );
      setFiles(filtered);
      setIsLoadingFiles(false);
    };

    if (actionInspectData) {
      loadFiles();
    }
  }, [actionInspectData]);

  const displayData = useMemo(() => {
    if (!selectedAction) return null;

    const inspectedArea = actionInspectData?.inspected_area
      ? parseFloat(String(actionInspectData.inspected_area))
      : 0;

    const farmerArea = actionInspectData?.farmer_area
      ? parseFloat(String(actionInspectData.farmer_area))
      : 0;

    const area = actionInspectData?.area
      ? parseFloat(String(actionInspectData.area))
      : 0;

    const parentInspectedArea = actionInspectData?.parent_inspected_area
      ? parseFloat(String(actionInspectData.parent_inspected_area))
      : 0;

    const totalInspectedArea = actionInspectData?.total_inspected_area
      ? parseFloat(String(actionInspectData.total_inspected_area))
      : 0;

    return {
      inspectedArea,
      farmerArea,
      parentInspectedArea,
      totalInspectedArea,
      area,
    };
  }, [selectedAction, actionInspectData]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Fixed header */}
        <DialogHeader className="flex flex-row items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-lg font-semibold text-dark-blue">
              {selectedAction?.action_name || ''}
            </DialogTitle>
            <Badge
              className={cn(
                'rounded-xl ring-0 border-0 px-2 py-0.5',
                getRegionActionStatus(selectedAction?.status)?.color,
                getRegionActionStatus(selectedAction?.status)?.color_bg,
              )}
            >
              {t(
                getRegionActionStatus(selectedAction?.status)?.label as string,
              )}
            </Badge>
          </div>
        </DialogHeader>

        {actionInspectLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">{t('loading')}</div>
          </div>
        ) : (
          <>
            {/* Fixed: Areas Data Section */}
            {displayData && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3 shrink-0">
                <div className="flex items-center gap-3 pb-2 border-b border-gray-200">
                  <h3 className="text-base font-medium text-dark-blue">
                    {t('inspection.area', 'Tekshirilgan maydon')}
                  </h3>
                </div>

                <div className="flex flex-col gap-2">
                  {displayData.area > 0 && (
                    <div className="flex justify-between items-center bg-gray-50 p-3 rounded text-sm text-gray-800">
                      <span className="font-medium">{t('general.area')}:</span>
                      <span className="font-semibold">
                        {displayData.area.toFixed(2)} ga
                      </span>
                    </div>
                  )}
                  {displayData.parentInspectedArea ? (
                    <>
                      <div className="flex justify-between items-center bg-gray-50 p-3 rounded text-sm text-gray-800">
                        <span className="font-medium">
                          Joriy oy uchun inspektor maydoni:
                        </span>
                        <span className="font-semibold">
                          {displayData.inspectedArea.toFixed(2)} ga
                        </span>
                      </div>
                      <div className="flex justify-between items-center bg-gray-50 p-3 rounded text-sm text-gray-800">
                        <span className="font-medium">
                          Jami nazorat qilingan maydon:
                        </span>
                        <span className="font-semibold">
                          {displayData.totalInspectedArea.toFixed(2)} ga
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center bg-gray-50 p-3 rounded text-sm text-gray-800">
                      <span className="font-medium">Inspektor maydoni:</span>
                      <span className="font-semibold">
                        {displayData.inspectedArea.toFixed(2)} ga
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center bg-gray-50 p-3 rounded text-sm text-gray-800">
                    <span className="font-medium">Fermer maydoni:</span>
                    <span className="font-semibold">
                      {displayData.farmerArea.toFixed(2)} ga
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Scrollable: Reviews Section */}
            {actionInspectData?.reviews &&
              actionInspectData.reviews.length > 0 && (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <div className="flex items-center gap-3 pb-2 border-b border-gray-200">
                      <h3 className="text-base font-medium text-dark-blue">
                        {t('identified.deficiencies')}
                      </h3>
                    </div>

                    <div className="space-y-6">
                      {actionInspectData.reviews.map((review, reviewIndex) => {
                        const reviewFileIds =
                          review.file_names?.map((f) => f.file_id) || [];
                        const reviewFiles = files.filter((f) =>
                          reviewFileIds.includes(f.fileId),
                        );

                        const Icon = getInspectionRateStatus(review.rate)?.icon;

                        return (
                          <div
                            key={reviewIndex}
                            className={cn(
                              'flex flex-col gap-3 border-gray-300 pt-4',
                              reviewIndex ? 'border-t' : '',
                            )}
                          >
                            {/* Images for this review */}
                            {reviewFiles.length > 0 && (
                              <div className="flex items-center flex-wrap gap-2">
                                {reviewFiles.map((file) => (
                                  <img
                                    key={file.fileId}
                                    src={file.base64}
                                    alt="Rasm"
                                    className="w-[80px] h-[60px] rounded-lg object-cover border border-gray-200 cursor-pointer"
                                    onClick={() => {
                                      const globalIndex = files.findIndex(
                                        (f) => f.fileId === file.fileId,
                                      );
                                      setSelectedImageIndex(
                                        globalIndex !== -1 ? globalIndex : 0,
                                      );
                                      setIsImageDialogOpen(true);
                                    }}
                                  />
                                ))}
                              </div>
                            )}

                            {/* Comments/Deficiencies and Date */}
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex flex-col gap-1">
                                <p className="text-sm font-semibold text-dark-blue">
                                  {review.reviewer_type && (
                                    <span className="font-medium text-gray-500">
                                      (
                                      {t(
                                        `INSPECTION_REVIEWER_${review.reviewer_type}`,
                                      )}
                                      ){' '}
                                    </span>
                                  )}
                                  {review.user_name}
                                </p>

                                {review.review_date && (
                                  <p className="text-xs text-gray-500 font-medium">
                                    {format(
                                      new Date(review.review_date),
                                      'dd.MM.yyyy HH:mm',
                                    )}
                                  </p>
                                )}
                              </div>
                              {getInspectionRateStatus(review.rate) && (
                                <div className="flex items-center gap-2">
                                  {Icon && <Icon />}
                                  <Badge
                                    className={cn(
                                      'rounded-xl ring-0 border-0 px-2 py-0.5 text-xs',
                                      getInspectionRateStatus(review.rate)
                                        ?.color,
                                      getInspectionRateStatus(review.rate)
                                        ?.color_bg,
                                    )}
                                  >
                                    {t(
                                      getInspectionRateStatus(review.rate)
                                        ?.label as string,
                                    )}
                                  </Badge>
                                </div>
                              )}
                            </div>

                            {/* Comment block */}
                            <div className="relative mt-1">
                              {review.comments && review.comments.length > 0 ? (
                                <div className="w-full border border-gray-300 rounded-lg min-h-10 p-3 text-sm text-gray-800 bg-gray-50 flex flex-col gap-2">
                                  {review.comments.map(
                                    (comment, commentIndex) => (
                                      <p key={comment.id || commentIndex}>
                                        {comment.name}
                                      </p>
                                    ),
                                  )}
                                </div>
                              ) : (
                                <div className="w-full border border-gray-300 rounded-lg min-h-10 p-3 text-sm text-gray-500 bg-gray-50 flex items-center">
                                  {t('no.comment', "Izoh yo'q")}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
          </>
        )}
      </DialogContent>

      {/* Image Preview */}
      <ImagePreview
        open={isImageDialogOpen}
        onClose={() => setIsImageDialogOpen(false)}
        images={files}
        initialIndex={selectedImageIndex ?? 0}
        onIndexChange={setSelectedImageIndex}
      />
    </Dialog>
  );
};
